import { Icon } from '@ipfabric/ui-components/Icon/Icon';
import type {
  IconGlyph,
  IconStyle,
} from '@ipfabric/ui-components/Icon/IconGlyph';
import { makeTypeGuardByProperty, safeAbsurd } from '@ipfabric/utils';
import classNames from 'classnames';
import { produce } from 'immer';
import { useEffect, useRef, useState } from 'react';
import { type CSSProperties, type FC, type ReactNode, forwardRef } from 'react';
import type {
  HorizontalDirection,
  VerticalDirection,
} from 'utils/types/Direction';
import type { Falsy } from 'utils/types/Falsy';
import './DropdownMenu.css';

type DropdownMenuItemCommon = {
  accent?: 'negative';
  dataTestId?: string;
  disabled?: boolean;
  label: ReactNode;
  icon?: IconGlyph;
  iconStyle?: IconStyle;
  loading?: boolean;
};

export type DropdownMenuButton = DropdownMenuItemCommon & {
  onClick?: () => void;
};

export type DropdownMenuCustomContent = {
  content: ReactNode;
};

/**
 * Submenu expanded on menu item hover
 */
export type DropdownSubmenu = DropdownMenuItemCommon & {
  children: (DropdownMenuItem | Falsy)[];
};

export type DropdownMenuItem =
  | DropdownMenuButton
  | DropdownMenuCustomContent
  | DropdownSubmenu
  | 'separator';

const isDropdownSubmenu = (item: DropdownMenuItem): item is DropdownSubmenu =>
  Object.hasOwnProperty.call(item, 'children');

const isDropdownMenuCustomContent = makeTypeGuardByProperty<
  DropdownMenuItem,
  DropdownMenuCustomContent
>('content');

const isDropdownMenuButton = (
  item: DropdownMenuItem,
): item is DropdownMenuButton =>
  Object.hasOwnProperty.call(item, 'label') && !isDropdownSubmenu(item);

const getItemClassName = (item: DropdownMenuItemCommon): string =>
  classNames(
    'ipf-dropdown-menu__item',
    item.accent === 'negative' && 'ipf-dropdown-menu__item--negative',
    item.disabled && 'ipf-dropdown-menu__item--disabled',
  );

interface SubmenuProps {
  container: HTMLElement | null;
  item: DropdownSubmenu;
  onClose: () => void;
}

export type MenuPosition = [HorizontalDirection, VerticalDirection];
export const defaultMenuPosition: MenuPosition = ['right', 'down'];

const CDropdownSubmenu: FC<SubmenuProps> = (props) => {
  const [open, setOpen] = useState(false);
  const [position, setPosition] = useState(defaultMenuPosition);
  const submenuRef = useRef<HTMLUListElement>(null);
  // biome-ignore lint/correctness/useExhaustiveDependencies: trigger only when open state changes
  useEffect(() => {
    setPosition(
      produce(position, (draft) => {
        if (!open || submenuRef.current === null) {
          return;
        }
        const containerBounds = props.container?.getBoundingClientRect() ?? {
          // when no container is provided, get viewport bounds
          right: window.innerWidth,
          bottom: window.innerHeight,
        };
        const submenuBounds = submenuRef.current.getBoundingClientRect();
        if (submenuBounds.right > containerBounds.right) {
          draft[0] = 'left';
        }
        if (submenuBounds.bottom > containerBounds.bottom) {
          draft[1] = 'up';
        }
      }),
    );
  }, [open]);

  return (
    <div
      className={classNames(getItemClassName(props.item), 'relative')}
      {...(!props.item.disabled && {
        onMouseEnter: () => {
          setOpen(true);
          setPosition(defaultMenuPosition);
        },
        onMouseLeave: () => {
          setOpen(false);
        },
      })}
    >
      <span className='flex-1'>{props.item.label}</span>
      <Icon className='-mr-3 ml-2' glyph='fa-chevron-right' size='xs' />
      {open && (
        <ul
          className={classNames(
            'ipf-dropdown-menu',
            `ipf-dropdown-menu__submenu--${position[0]}`,
            `ipf-dropdown-menu__submenu--${position[1]}`,
          )}
          ref={submenuRef}
        >
          {renderMenuItems(props.item.children, props.container, props.onClose)}
        </ul>
      )}
    </div>
  );
};

const renderMenuItem = (
  item: DropdownMenuItem,
  container: HTMLElement | null,
  onClose: () => void,
) => {
  if (isDropdownMenuButton(item)) {
    return (
      <div
        className={getItemClassName(item)}
        data-testid={item.dataTestId}
        onClick={() => {
          if (!item.disabled) {
            item.onClick?.();
            onClose();
          }
        }}
      >
        {item.icon !== undefined && (
          <Icon
            loading={item.loading}
            glyph={item.icon}
            iconStyle={item.iconStyle}
            size='sm'
            className='mr-2'
          />
        )}
        {item.label}
      </div>
    );
  }
  if (isDropdownSubmenu(item)) {
    return (
      <CDropdownSubmenu item={item} container={container} onClose={onClose} />
    );
  }
  if (isDropdownMenuCustomContent(item)) {
    return item.content;
  }
  if (item === 'separator') {
    return <div className='ipf-dropdown-menu__separator' />;
  }
  safeAbsurd(item);
  return null;
};

const renderMenuItems = (
  items: (DropdownMenuItem | Falsy)[],
  container: HTMLElement | null,
  onClose: () => void,
): ReactNode =>
  items.map((item, index) =>
    item ? (
      // it is useful to use the index from input array here, in other words
      // do not filter falsy items before mapping to react nodes, to keep indexes stable.
      // biome-ignore lint/suspicious/noArrayIndexKey: <explanation>
      <li key={index}>{renderMenuItem(item, container, onClose)}</li>
    ) : null,
  );

export interface DropdownMenuProps {
  className?: string;
  disableContextMenu?: boolean;
  /**
   * List of menu items
   *
   * Falsy allowed to provide list of items with optional entries, like e.g. classNames library.
   */
  items: (DropdownMenuItem | Falsy)[];
  onClose: () => void;
  /**
   * Optionally pass position of menu via style
   */
  style?: CSSProperties;
}

export const DropdownMenu = forwardRef<HTMLUListElement, DropdownMenuProps>(
  (props, ref) => (
    <ul
      className={classNames('ipf-dropdown-menu', props.className)}
      style={props.style}
      ref={ref}
      {...(props.disableContextMenu && {
        onContextMenu: (e) => {
          e.preventDefault();
        },
      })}
    >
      {renderMenuItems(props.items, null, props.onClose)}
    </ul>
  ),
);

DropdownMenu.displayName = 'DropdownMenu';
